import React, { useEffect, useRef, useState } from 'react';
import AudioPlayer from '../AudioPlayer/AudioPlayer';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import './ProjectModal.css';

const MediaItem = ({ item }) => {
  const [isVisible, setIsVisible] = useState(false);
  const mediaRef = useRef(null);
  const playerRef = useRef(null);
  const [youtubePlayer, setYoutubePlayer] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (mediaRef.current) {
      observer.observe(mediaRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleTimestampClick = (timestamp) => {
    if (!youtubePlayer) return;

    const match = timestamp.match(/\[(\d{2}):(\d{2})\]/);
    if (!match) return;

    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    const totalSeconds = minutes * 60 + seconds;

    youtubePlayer.seekTo(totalSeconds, true);
    youtubePlayer.playVideo();
  };

  const handlePlayerReady = (player) => {
    setYoutubePlayer(player);
  };

  switch (item.type) {
    case 'image':
      return (
        <div className="media-item media-item--image" ref={mediaRef}>
          <div className="media-info">
            <h4>{item.caption}</h4>
            {item.description && <p>{item.description}</p>}
          </div>
          {isVisible && (
            <img
              src={item.url}
              alt={item.caption}
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
      );

    case 'video':
      return (
        <div className="media-item media-item--video" ref={mediaRef}>
          <div className="media-info">
            <h4>{item.caption}</h4>
            {item.description && (
              <p className="modal-media-description">
                {item.description.split(/(\[\d{2}:\d{2}\])/).map((part, index) => {
                  if (part.match(/\[\d{2}:\d{2}\]/)) {
                    return (
                      <button
                        type="button"
                        key={`${part}-${index}`}
                        className="timestamp"
                        onClick={() => handleTimestampClick(part)}
                      >
                        {part}
                      </button>
                    );
                  }

                  return part;
                })}
              </p>
            )}
          </div>

          {isVisible && (
            <div className="modal-media-container">
              <VideoPlayer
                ref={playerRef}
                url={item.url}
                controls={true}
                width="100%"
                height="100%"
                style={{ aspectRatio: '16/9' }}
                onReady={handlePlayerReady}
                config={{
                  youtube: {
                    playerVars: {
                      showinfo: 1,
                      rel: 0,
                      enablejsapi: 1,
                      origin: window.location.origin,
                    },
                  },
                }}
              />
            </div>
          )}
        </div>
      );

    case 'audio':
      return (
        <div className="media-item media-item--audio" ref={mediaRef}>
          <div className="media-info">
            <h4>{item.caption}</h4>
            {item.description && <p>{item.description}</p>}
          </div>
          {isVisible && (
            <div className="custom-audio-container">
              <AudioPlayer src={item.url} title={item.caption} />
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
};

const ProjectModal = ({ project, onClose }) => {
  const [lyricsExpanded, setLyricsExpanded] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    setLyricsExpanded(false);
  }, [project]);

  const getAllMedia = (media) => {
    if (!media) return [];

    return [
      ...(media.images || []),
      ...(media.videos || []),
      ...(media.audio || []),
    ];
  };

  const normalizedLinks = !project.links
    ? []
    : Array.isArray(project.links)
      ? project.links.filter((link) => link && link.url)
      : [
          project.links.github && {
            url: project.links.github,
            label: 'GitHub Repository',
          },
          project.links.live && {
            url: project.links.live,
            label: 'Live Demo',
          },
        ].filter(Boolean);

  const allMedia = getAllMedia(project.media);
  const projectMetrics = [
    { label: 'skills', value: project.skills?.length || 0 },
    { label: 'media', value: allMedia.length },
    { label: 'links', value: normalizedLinks.length },
  ].filter((item) => item.value > 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
      >
        <button
          className="close-button"
          onClick={onClose}
          aria-label="Close project details"
        >
          ×
        </button>

        <div className="project-modal-header">
          <div className="project-modal-header-main">
            <div className="project-modal-brand">
              <div className="project-modal-icon-shell">
                <img src={project.icon} alt={project.title} className="project-modal-icon" />
              </div>

              <div className="project-modal-copy">
                <span className="project-modal-label">Project</span>
                <h2 className="project-modal-title" id="project-modal-title">
                  {project.title}
                </h2>
              </div>
            </div>

            {normalizedLinks.length > 0 && (
              <div className="project-modal-actions">
                {normalizedLinks.map((link, index) => (
                  <a
                    key={`${link.url}-${index}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className="project-modal-action"
                  >
                    {link.label || 'External Link'}
                  </a>
                ))}
              </div>
            )}
          </div>

          {projectMetrics.length > 0 && (
            <div className="project-modal-meta" aria-label="Project metrics">
              {projectMetrics.map((metric) => (
                <div key={metric.label} className="project-modal-metric">
                  <span>{metric.label}</span>
                  <strong>{String(metric.value).padStart(2, '0')}</strong>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="project-details">
          <div className="modal-top-grid">
            <section className="modal-section modal-section--primary">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Overview</span>
                <h3>Description</h3>
              </div>
              <p className="modal-project-description">{project.description}</p>
            </section>

            {project.history && (
              <section className="modal-section">
                <div className="modal-section-heading">
                  <span className="modal-section-kicker">Background</span>
                  <h3>History</h3>
                </div>
                <p className="project-history">{project.history}</p>
              </section>
            )}
          </div>

          {project.skills && (
            <section className="modal-section">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Tools</span>
                <h3>Skills and technologies</h3>
              </div>
              <div className="skills-list">
                {project.skills.map((skill, index) => (
                  <span key={`${skill}-${index}`} className="modal-skill-tag">
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          {project.workflow && (
            <section className="modal-section">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Process</span>
                <h3>Workflow</h3>
              </div>
              {Array.isArray(project.workflow) ? (
                <ol className="workflow-timeline">
                  {project.workflow.map((step, index) => (
                    <li className="workflow-step" key={`${step}-${index}`}>
                      <div className="workflow-badge" aria-hidden="true">
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <div className="workflow-content">
                        <p>{step}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="workflow-text">{project.workflow}</p>
              )}
            </section>
          )}

          {project.media && allMedia.length > 0 && (
            <section className="modal-section">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Media</span>
                <h3>Project media</h3>
              </div>
              <div className="project-media-grid">
                {allMedia.map((item, index) => (
                  <MediaItem key={`${item.url}-${index}`} item={item} />
                ))}
              </div>
            </section>
          )}

          {project.lyrics && (
            <section className="modal-section">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Lyrics</span>
                <h3>Text</h3>
              </div>

              <div className={`lyrics-container ${lyricsExpanded ? 'expanded' : 'collapsed'}`}>
                <pre className="lyrics-text">{project.lyrics}</pre>
                {!lyricsExpanded && <div className="lyrics-fade" aria-hidden="true" />}
              </div>

              <button
                className="lyrics-toggle"
                onClick={() => setLyricsExpanded((value) => !value)}
                aria-expanded={lyricsExpanded}
              >
                {lyricsExpanded ? 'Hide lyrics' : 'Show lyrics'}
              </button>
            </section>
          )}

          {project.images && project.images.length > 0 && (
            <section className="modal-section">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Gallery</span>
                <h3>Supporting visuals</h3>
              </div>

              <div className="project-gallery">
                {project.images.map((image, index) => (
                  <div key={`${image.url}-${index}`} className="gallery-item">
                    <img src={image.url} alt={image.caption} />
                    <div className="gallery-item-info">
                      <h4>{image.caption}</h4>
                      <p>{image.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {normalizedLinks.length > 0 && (
            <section className="modal-section modal-section--links">
              <div className="modal-section-heading">
                <span className="modal-section-kicker">Links</span>
                <h3>External destinations</h3>
              </div>

              <div className="project-links">
                {normalizedLinks.map((link, index) => (
                  <a
                    key={`${link.url}-${index}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                  >
                    {link.label || 'External Link'}
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
